import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { faqs } from '@/mocks/faqs';

export default function Home() {
  const hotArticles = articles.slice(0, 6);

  return (
    <>
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: '磁力链 - 磁力链接使用指南与资源搜索入口',
          description: '磁力链是一种基于哈希值的点对点资源分享协议，无需中央服务器即可完成文件下载。',
          url: 'https://magnetlink-guide.com/',
          inLanguage: 'zh-CN',
          isPartOf: {
            '@type': 'WebSite',
            name: '磁力链指南',
            url: 'https://magnetlink-guide.com/',
          },
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, item: { '@id': 'https://magnetlink-guide.com/', name: '首页' } },
            ],
          },
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        })}
      </script>

      {/* Hero Section */}
      <section className="relative w-full min-h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20digital%20network%20with%20interconnected%20glowing%20nodes%20and%20flowing%20data%20streams%2C%20warm%20amber%20and%20gold%20gradient%2C%20dark%20sophisticated%20background%2C%20minimalist%20futuristic%20technology%20concept%20art%2C%20clean%20geometric%20patterns%2C%20soft%20particle%20effects%2C%20cinematic%20lighting&width=1600&height=900&seq=hero-magnet-home&orientation=landscape"
            alt="磁力链技术概念图 - 去中心化P2P网络可视化"
            title="磁力链 - 去中心化P2P网络技术"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
        </div>

        <div className="relative z-10 w-full px-4 md:px-6 py-20 text-center">
          <div className="max-w-3xl mx-auto">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              磁力链使用指南与资源搜索入口
            </h1>
            <p className="text-base md:text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              磁力链是一种基于哈希值的点对点资源分享协议，无需中央服务器即可完成文件下载。掌握磁力链的工作原理、使用方法与搜索技巧，开启高效资源获取之旅。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/tutorials"
                className="px-8 py-3.5 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap text-sm no-underline"
              >
                开始学习
                <span className="w-4 h-4 inline-flex items-center justify-center ml-2">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </Link>
              <Link
                to="/category/clients"
                className="px-8 py-3.5 bg-white/15 text-white border border-white/30 rounded-full hover:bg-white/25 transition-all duration-300 whitespace-nowrap text-sm no-underline backdrop-blur-sm"
              >
                客户端推荐
              </Link>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              { value: '50万+', label: '索引资源' },
              { value: '1000+', label: 'Tracker节点' },
              { value: '19篇', label: '深度教程' },
              { value: '10万+', label: '月活用户' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white font-heading">{stat.value}</div>
                <div className="text-xs md:text-sm text-white/60 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              内容分类导航
            </h2>
            <p className="text-sm text-foreground-600 max-w-xl mx-auto">
              按主题浏览磁力链相关内容，从入门到精通全面覆盖
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-300 no-underline"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                  <i className={`${cat.icon} text-lg`}></i>
                </div>
                <h3 className="font-heading text-base font-bold text-foreground-900 mb-1.5">
                  {cat.name}
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">
                  {cat.description}
                </p>
                <div className="mt-3 flex items-center gap-1 text-xs text-primary-600 font-medium">
                  <span>{cat.articleCount} 篇文章</span>
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Hot Articles */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-10 gap-4">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                热门文章
              </h2>
              <p className="text-sm text-foreground-600">精选磁力链使用教程与技巧，助你快速上手</p>
            </div>
            <Link
              to="/category/tutorials"
              className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200 whitespace-nowrap no-underline"
            >
              查看全部
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {hotArticles.map((article) => {
              const cat = categories.find((c) => c.slug === article.category);
              return (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 no-underline"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      {cat && (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
                          {cat.name}
                        </span>
                      )}
                      <span className="text-xs text-foreground-400">{article.readTime}</span>
                    </div>
                    <h3 className="font-heading text-base font-bold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                      {article.summary}
                    </p>
                    <div className="mt-3 flex items-center gap-3 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-calendar-line"></i>
                        </span>
                        {article.publishDate}
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              常见问题
            </h2>
            <p className="text-sm text-foreground-600 max-w-xl mx-auto">
              关于磁力链的常见疑问，这里都有详细解答
            </p>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden"
              >
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none hover:bg-background-100/70 transition-colors duration-200">
                  <h4 className="font-heading text-base font-bold text-foreground-800 pr-8">
                    {faq.question}
                  </h4>
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:rotate-180 transition-transform duration-200">
                    <i className="ri-arrow-down-s-line"></i>
                  </span>
                </summary>
                <div className="px-6 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/category/faq-category"
              className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200 no-underline"
            >
              查看更多问题解答
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 md:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20abstract%20gradient%20background%20with%20soft%20flowing%20curves%2C%20amber%20and%20gold%20tones%2C%20minimalist%20elegant%20design%2C%20subtle%20geometric%20texture%2C%20premium%20atmospheric%20feel&width=1600&height=600&seq=cta-magnet-home&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-background-900/80"></div>
        </div>

        <div className="relative z-10 w-full px-4 md:px-6 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            准备好开始使用磁力链了吗？
          </h2>
          <p className="text-sm md:text-base text-white/70 mb-8 max-w-lg mx-auto">
            从入门教程开始，逐步掌握磁力链的全部使用技巧
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/category/tutorials"
              className="px-8 py-3.5 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-400 transition-all duration-300 whitespace-nowrap text-sm no-underline"
            >
              浏览使用教程
            </Link>
            <Link
              to="/about"
              className="px-8 py-3.5 bg-white/10 text-white border border-white/20 rounded-full hover:bg-white/20 transition-all duration-300 whitespace-nowrap text-sm no-underline backdrop-blur-sm"
            >
              了解更多
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}